/* ============================================================================
   SNAPGOODY — SHOPIFY BRIDGE
   ----------------------------------------------------------------------------
   The front end (core.js / editor.js / shop.js / site.js) is shared byte for
   byte with the static build. This file is the only Shopify-specific layer: it
   rebuilds SG.catalog, SG.media and SG.cart from Liquid-emitted JSON instead of
   the local content/ files.

   WHAT LIQUID EMITS
   -----------------
     <script type="application/json" id="sg-data">{
        market, currency,
        collections: [{handle,title,blurb,image}],
        products:    [{...see normalise() below}],
        fittingRoom: [{key,title,example,exampleLabel,image}],
        slots:       { "hero.slide-1": {src,label,size,focal}, ... }
     }</script>

   Everything a product needs for the studio lives in metafields — see
   PRINTIFY.md for the full metafield list and the order handoff.
   ========================================================================== */
(function (global) {
  'use strict';
  var SG = global.SG;
  if (!SG) { console.error('[snapgoody] core.js must load before theme-bridge.js'); return; }

  var shop = global.SG_SHOP || {};
  var raw = {};
  var node = document.getElementById('sg-data');
  if (node) { try { raw = JSON.parse(node.textContent); } catch (e) { console.error('[snapgoody] bad sg-data', e); } }

  /* ================================================================ REGION ==*/
  var MARKET_TO_REGION = { uk: 'UK', gb: 'UK', eu: 'EU', europe: 'EU', us: 'US', 'north-america': 'US' };
  SG.region = MARKET_TO_REGION[String(shop.market || '').toLowerCase()] || 'UK';

  /* Shopify already prices in the customer's currency, so no conversion here.
     Prices arrive as integer minor units in the active presentment currency. */
  Object.keys(SG.regions).forEach(function (k) { SG.regions[k].rate = 1; });
  if (shop.currency) {
    SG.regions[SG.region].currency = shop.currency;
    SG.regions[SG.region].locale = document.documentElement.lang || 'en-GB';
  }

  /* ================================================================= MEDIA ==*/
  global.SG_MEDIA = {
    basePath: '',
    slots: raw.slots || {},
    products: {},
    mockups: {}
  };
  (raw.products || []).forEach(function (p) {
    global.SG_MEDIA.products[p.handle] = p.images || [];
    if (p.mockup) global.SG_MEDIA.mockups[p.handle] = p.mockup;
  });

  /* =============================================================== CONTENT ==*/
  global.SG_CONTENT = global.SG_CONTENT || {};

  /* =============================================================== CATALOG ==*/
  function normalise(p) {
    var geo = geometryFor(p);
    return {
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      price: p.price,                       // integer minor units
      compareAt: p.compareAt || null,
      collections: p.collections || [],
      badge: p.badge || '',
      family: p.family || '',
      familyLabel: p.familyLabel || '',
      luxury: !!p.luxury,
      blurb: p.blurb || '',
      description: p.description || '',
      details: p.details || [],
      options: p.options || [],
      surfaces: applyStudio(geo.surfaces),
      /* Market-scoped overrides — see PRINTIFY.md. Absent for most products. */
      optionsByMarket: p.optionsByMarket || null,
      surfacesByMarket: (function (m) {
        if (!m) return null;
        var out = {};
        Object.keys(m).forEach(function (k) { out[k] = applyStudio(m[k]); });
        return out;
      }(geo.surfacesByMarket)),
      detailsByMarket: p.detailsByMarket || null,
      optionNotes: p.optionNotes || null,
      allowBackground: geo.allowBackground !== false,
      mock: geo.mock || { ratio: 1, radius: '4px' },
      bundle: p.bundle || null,
      regions: p.regions || {},
      personalisable: p.personalisable !== false,
      variants: p.variants || [],           // [{id, options:[..], available}]
      defaultVariantId: p.defaultVariantId || null,
      url: p.url || ('/products/' + p.handle)
    };
  }
  function defaultSurface() {
    return {
      id: 'front', label: 'Print area', widthMM: 200, heightMM: 200, dpi: 300,
      area: { x: 0.1, y: 0.1, w: 0.8, h: 0.8 }, shape: 'rect', radius: 0, safe: 0.06, bg: '#FFFFFF'
    };
  }

  /* ══════════════════════════════════════════════════ PRINT GEOMETRY ══
     The studio's canvas IS the print surface: real millimetres, a shape and a
     corner radius. An AirPod case is a small rounded square, a phone case a
     tall narrow panel, a coaster a circle, a calendar twelve pages.

     Those numbers live in each product's `snapgoody.surfaces` metafield. Until
     a metafield is filled in there is nothing to read, and every product was
     falling back to one generic 200×200 square — which is why the live preview
     stopped changing shape between products.

     So: metafield first, then the Snapgoody piece this product matches (by
     handle or title, from the library in snippets/sg-geometry.liquid), and only
     then the square. Filling the metafield in later changes nothing that is
     already right; it just takes precedence.
     ================================================================== */
  /* ══════════════════════════════════════════════ STUDIO SETTINGS ══
     Theme settings → Studio. Applied to the print surfaces as they are built,
     so the shared editor needs no knowledge of them. */
  /* The Fitting Room section may override them for its own page — see the
     comment in sections/fitting-room-page.liquid. It emits nothing unless the
     merchant explicitly ticks the override, so on every other page, and on this
     one until they do, the store-wide values are the only ones in play. */
  var studioPage = {};
  (function () {
    var el = document.getElementById('sg-studio-page');
    if (!el) return;
    try { studioPage = JSON.parse(el.textContent) || {}; }
    catch (e) { console.warn('[snapgoody] could not read this page\'s studio settings', e); }
  }());

  var STUDIO = Object.assign({
    photoScale: 100, showGuides: true, safeMargin: 0, guideStrength: 100,
    centerGuides: true, requireDesign: true,
    requireTitle: 'Personalise this piece first',
    requireBody: 'This piece is made to order from your own design, so there is nothing to print yet. Add a photograph or a line of text, then save it.',
    requireCta: 'Personalise it now', requireDismiss: 'Not yet'
  }, raw.studio || {}, studioPage);
  SG.studio = STUDIO;

  /* Scales the print area about its own centre, and applies a safe-margin
     override. Clamped so a scale can never push the area off the mockup. */
  function applyStudio(surfaces) {
    var scale = (Number(STUDIO.photoScale) || 100) / 100;
    var safe = Number(STUDIO.safeMargin) || 0;
    return (surfaces || []).map(function (s) {
      var out = Object.assign({}, s);
      if (safe > 0) out.safe = safe / 100;
      if (scale !== 1 && s.area) {
        var a = s.area;
        var cx = a.x + a.w / 2, cy = a.y + a.h / 2;
        var w = Math.min(1, a.w * scale), h = Math.min(1, a.h * scale);
        out.area = {
          x: Math.max(0, Math.min(1 - w, cx - w / 2)),
          y: Math.max(0, Math.min(1 - h, cy - h / 2)),
          w: w, h: h
        };
      }
      return out;
    });
  }

  var GEO = { order: [], pieces: {} };
  (function () {
    var el = document.getElementById('sg-geometry');
    if (!el) return;
    try { GEO = JSON.parse(el.textContent) || GEO; }
    catch (e) { console.warn('[snapgoody] could not read the print geometry library', e); }
  }());

  function matchPiece(p) {
    var hay = ((p.handle || '') + ' ' + (p.title || '')).toLowerCase().replace(/[-_]+/g, ' ');
    for (var i = 0; i < (GEO.order || []).length; i++) {
      var key = GEO.order[i];
      var piece = (GEO.pieces || {})[key];
      if (!piece) continue;
      for (var j = 0; j < (piece.match || []).length; j++) {
        if (hay.indexOf(String(piece.match[j]).toLowerCase()) !== -1) return { key: key, piece: piece };
      }
    }
    return null;
  }

  /* Remembers what each product resolved to, so the doctor and the console can
     report it rather than leaving you guessing why a preview looks wrong. */
  var geoReport = [];

  function geometryFor(p) {
    if (p.surfaces && p.surfaces.length) {
      geoReport.push({ handle: p.handle, from: 'metafield', surfaces: p.surfaces.length });
      return { surfaces: p.surfaces, surfacesByMarket: p.surfacesByMarket || null,
               mock: p.mock, allowBackground: p.allowBackground };
    }
    var hit = matchPiece(p);
    if (hit) {
      geoReport.push({ handle: p.handle, from: 'matched ' + hit.key, surfaces: (hit.piece.surfaces || []).length });
      return {
        surfaces: JSON.parse(JSON.stringify(hit.piece.surfaces || [defaultSurface()])),
        surfacesByMarket: hit.piece.surfacesByMarket ? JSON.parse(JSON.stringify(hit.piece.surfacesByMarket)) : null,
        /* A mock the merchant actually set always wins, including a square
           one — which is why product-json emits null rather than a default. */
        mock: p.mock ? p.mock : (hit.piece.mock || null),
        allowBackground: hit.piece.allowBackground === false ? false : p.allowBackground
      };
    }
    geoReport.push({ handle: p.handle, from: 'generic square — no metafield and no match', surfaces: 1 });
    return { surfaces: [defaultSurface()], surfacesByMarket: null, mock: p.mock, allowBackground: p.allowBackground };
  }

  var PRODUCTS = (raw.products || []).map(normalise);
  var byHandle = {};
  PRODUCTS.forEach(function (p) { byHandle[p.handle] = p; });

  var COLLECTIONS = (raw.collections || []).map(function (c) {
    return { handle: c.handle, title: c.title, blurb: c.blurb || '', tagline: c.tagline || '', slot: 'collection.' + c.handle };
  });
  COLLECTIONS.forEach(function (c) {
    if (c.image) global.SG_MEDIA.slots['collection.' + c.handle] = { src: c.image, label: c.title };
  });

  function pick(map, fallback, region) {
    if (!map) return fallback;
    return map[region] || map[SG.region] || fallback;
  }

  SG.catalog = {
    products: PRODUCTS,
    collections: COLLECTIONS,
    fittingRoom: raw.fittingRoom || [],
    months: ['January', 'February', 'March', 'April', 'May', 'June',
             'July', 'August', 'September', 'October', 'November', 'December'],

    get: function (h) { return byHandle[h] || null; },

    /* ── market-aware accessors. Always read a product through these. ───── */
    optionsFor: function (product, region) {
      if (!product) return [];
      return pick(product.optionsByMarket, product.options || [], region || SG.region);
    },
    surfacesFor: function (product, region) {
      if (!product) return [];
      return pick(product.surfacesByMarket, product.surfaces || [], region || SG.region);
    },
    detailsFor: function (product, region) {
      if (!product) return [];
      return pick(product.detailsByMarket, product.details || [], region || SG.region);
    },
    surfaceBg: function (surf, options) {
      if (!surf || !surf.bgFrom) return null;
      var v = (options || {})[surf.bgFrom.option];
      return (surf.bgFrom.map || {})[v] || null;
    },
    optionKey: function (product, options, region, names) {
      var list = SG.catalog.optionsFor(product, region);
      if (names) list = list.filter(function (o) { return names.indexOf(o.name) !== -1; });
      return list.map(function (o) { return (options || {})[o.name] || o.values[0]; }).join('|');
    },
    inCollection: function (h) {
      return PRODUCTS.filter(function (p) { return (p.collections || []).indexOf(h) !== -1; });
    },
    collection: function (h) {
      return COLLECTIONS.filter(function (c) { return c.handle === h; })[0] || null;
    },
    family: function (n) { return PRODUCTS.filter(function (p) { return p.family === n; }); },
    search: function (q) {
      q = String(q || '').trim().toLowerCase();
      if (!q) return [];
      return PRODUCTS.filter(function (p) {
        return (p.title + ' ' + p.blurb + ' ' + p.vendor + ' ' + p.handle).toLowerCase().indexOf(q) !== -1;
      }).slice(0, 8);
    },

    /* Which Shopify product fulfils this handle in the active market? ------ */
    forRegion: function (product, region) {
      if (!product) return null;
      var r = (product.regions || {})[region || SG.region];
      return r || (product.regions || {})[SG.region] || null;
    },

    /* Resolve the Shopify variant for the chosen options in this market. --- */
    lineFor: function (product, region, options) {
      region = region || SG.region;
      var r = SG.catalog.forRegion(product, region);
      var key = SG.catalog.optionKey(product, options, region);

      var variantId = null;
      if (r && r.variantMap && r.variantMap[key]) variantId = r.variantMap[key];
      else if (r && r.defaultVariantId) variantId = r.defaultVariantId;
      else {
        /* Fall back to the product's own variants when no regional map exists. */
        var wanted = SG.catalog.optionsFor(product, region).map(function (o) { return (options || {})[o.name] || o.values[0]; });
        var hit = (product.variants || []).filter(function (v) {
          return v.options.join('|') === wanted.join('|');
        })[0];
        variantId = hit ? hit.id : product.defaultVariantId;
      }

      var base = {
        handle: product.handle,
        title: product.title,
        market: region,
        shopifyProductId: r ? r.shopifyProductId : null,
        shopifyVariantId: variantId,
        printify: r ? r.printify : null,
        optionKey: key,
        options: Object.assign({}, options)
      };

      if (product.bundle) {
        base.components = (product.bundle.components || []).map(function (c) {
          var cr = (c.regions || {})[region] || {};
          /* Each half is resolved by only the options that belong to it, so the
             case and the wallet reach their own Shopify variants. */
          var ckey = SG.catalog.optionKey(product, options, region, c.options || null);
          return {
            key: c.key,
            title: c.title,
            sameAs: c.sameAs || null,
            surface: c.surface || null,
            optionKey: ckey,
            optionNames: c.options || [],
            shopifyProductId: cr.shopifyProductId || null,
            shopifyVariantId: (cr.variantMap && cr.variantMap[ckey]) || cr.defaultVariantId || null,
            printify: cr.printify || null
          };
        });
      }
      return base;
    },

    recommend: function (handle, count) {
      var p = byHandle[handle];
      var pool = PRODUCTS.filter(function (x) { return x.handle !== handle && x.personalisable; });
      var score = function (x) {
        var s = 0;
        if (p) {
          (x.collections || []).forEach(function (c) { if ((p.collections || []).indexOf(c) !== -1) s += 2; });
          if (x.vendor === p.vendor) s += 1;
        }
        if ((x.collections || []).indexOf('top') !== -1) s += 2;
        if (x.badge) s += 1;
        return s;
      };
      return pool.sort(function (a, b) { return score(b) - score(a); }).slice(0, count || 3);
    }
  };

  /* ============================================================ SHOPIFY URLS ==
     The front end is shared byte for byte with the static build, so it asks for
     destinations in that build's language ("product.html?p=custom-mug"). This
     translates each request into a URL SHOPIFY ITSELF produced, emitted in
     sg-data by Liquid.

     Two rules, both learned the hard way:

       1. Never build a path out of a handle. A store's collection handles are
          whatever the merchant's titles generated — "Top Products" becomes
          `top-products`, not `top` — so a constructed path is a guess, and a
          wrong guess is a 404.

       2. Never silently substitute a different destination. A link that cannot
          be resolved returns '' and the caller declines to render it. Sending a
          shopper to the homepage, or to /collections/all, instead of where the
          link said, is worse than showing no link: it looks like the site is
          broken and hides the real problem.
     ========================================================================= */
  var ROUTES  = raw.routes  || {};
  var TARGETS = raw.targets || {};
  var PAGEMAP = raw.pages   || {};
  var root = ROUTES.root || (shop.routes && shop.routes.root) || '/';

  /* Collection handle → the URL Shopify emitted for it. */
  var colUrl = {};
  (raw.collections || []).forEach(function (c) { if (c.url) colUrl[c.handle] = c.url; });

  /* The static build's five collection handles are roles, not addresses. Each
     resolves through Theme settings → Navigation targets. */
  var ROLE_OF_HANDLE = {
    'home-lifestyle': 'home-everyday',
    'home-everyday':  'home-everyday',
    'top':            'top',
    'accessories':    'accessories',
    'apple':          'apple',
    'gifting':        'gifting'
  };

  var warned = {};
  function unresolved(what, asked) {
    if (!warned[asked]) {
      warned[asked] = true;
      console.warn('[snapgoody] no ' + what + ' for "' + asked + '". ' +
        'Set it under Theme settings → Navigation targets, or create it in Shopify. ' +
        'Open the theme editor, or tick Theme settings \u2192 Storefront doctor, for '
        + 'the full report. The link has been hidden ' +
        'rather than pointed somewhere wrong.');
    }
    return '';
  }

  SG.collectionUrl = function (handle) {
    if (!handle) return '';
    var role = ROLE_OF_HANDLE[handle];
    if (role && TARGETS[role]) return TARGETS[role];
    if (colUrl[handle]) return colUrl[handle];
    return unresolved('collection', handle);
  };

  SG.pageUrl = function (slug) {
    if (!slug) return '';
    if (PAGEMAP[slug]) return PAGEMAP[slug];
    return unresolved('page', slug);
  };

  SG.productUrl = function (handle) {
    if (!handle) return '';
    var p = byHandle[handle];
    if (p && p.url) return p.url;
    /* Product URLs are the one case Shopify guarantees by convention: a
       product's handle IS its path. Safe to build, unlike a collection. */
    return root + 'products/' + handle;
  };

  SG.url = function (p) {
    if (!p) return root;
    if (/^(https?:|mailto:|tel:|#|\/)/.test(p)) return p;
    var m = String(p).match(/^(index|product|collection|fitting-room|page|account)\.html(?:\?(.*))?$/);
    if (!m) return p;
    var q = new URLSearchParams(m[2] || '');
    switch (m[1]) {
      case 'index':        return root;
      case 'product':      return SG.productUrl(q.get('p'));
      case 'collection':   return SG.collectionUrl(q.get('c') || '');
      case 'account':      return SG.accountUrl();
      case 'fitting-room': {
        var fr = TARGETS['fitting-room'] || PAGEMAP['fitting-room'];
        if (!fr) return unresolved('Fitting Room page', 'fitting-room');
        return q.get('p') ? fr + '?p=' + encodeURIComponent(q.get('p')) : fr;
      }
      case 'page': {
        var slug = q.get('p') || '';
        if (slug === 'checkout') return SG.checkoutUrl();
        return SG.pageUrl(slug);
      }
    }
    return p;
  };
  SG.go = function (p) { var u = SG.url(p); if (u) global.location.href = u; };
  SG.query = function (k) { return new URLSearchParams(global.location.search).get(k); };

  /* What each product's print geometry came from. Read by the storefront
     doctor; also worth a look in the console when a preview looks wrong. */
  SG.geometryReport = function () { return geoReport.slice(); };
  (function () {
    var generic = geoReport.filter(function (r) { return r.from.indexOf('generic') === 0; });
    if (generic.length) {
      console.warn('[snapgoody] ' + generic.length + ' product(s) have no print geometry — ' +
        'the studio will show a plain square for them. Add a `snapgoody.surfaces` metafield, ' +
        'or rename so the piece is recognised: ' + generic.map(function (r) { return r.handle; }).join(', '));
    }
  }());

  SG.accountUrl  = function () { return ROUTES.account  || (root + 'account'); };
  SG.checkoutUrl = function () { return ROUTES.checkout || '/checkout'; };
  SG.searchUrl   = function () { return ROUTES.search   || (root + 'search'); };
  SG.allProductsUrl = function () { return ROUTES.allProducts || (root + 'collections/all'); };

}(window));
